import classNames from 'classnames';
import { isNull } from 'lodash';
import { forwardRef, useMemo } from 'react';

import { type LibraryWithPermissions } from '@air/api/types';
import { Skeleton } from '@air/component-skeleton';
import { CheckIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { convertUnknownToError } from '@air/utils-error';

import { useLibraryLeaveMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryLeaveMutation';
import { useCanLeaveLibrary } from '~/components/LibraryBeta/hooks/useCanLeaveLibrary';
import { LibraryItem, type LibraryItemProps } from '~/components/LibraryBeta/LibraryBrowseModal/components/LibraryItem';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useAccount } from '~/swr-hooks/account/useAccount';

export type LibraryItemMemberContainerProps = Pick<LibraryItemProps, 'className'> & {
  library: LibraryWithPermissions;
  onLeave: () => void;
  stats: null | string[];
};

export const LibraryItemMemberContainer = forwardRef<HTMLDivElement, LibraryItemMemberContainerProps>(
  ({ className, library, onLeave, stats }, ref) => {
    const { showLibraryToast } = useLibraryToasts();
    const { libraryLeaveMutation } = useLibraryLeaveMutation({ libraryId: library.id });
    const { data: account } = useAccount();
    const { canLeaveLibrary } = useCanLeaveLibrary({ libraryId: library.id });

    const meta = useMemo(() => {
      if (isNull(stats)) {
        return null;
      }

      return [
        <div key="0" className="mr-1 flex items-center gap-1 font-semibold text-12 text-parrot-700">
          <CheckIcon size="sm" />
          Joined
        </div>,
        ' • ',
        ...stats,
      ];
    }, [stats]);

    return (
      <LibraryItem
        actions={
          <Button
            appearance="ghost"
            color="gray"
            disabled={!account?.id || !canLeaveLibrary}
            isLoading={libraryLeaveMutation.isPending}
            data-testid="LIBRARY_ITEM_MEMBER_LEAVE_BUTTON"
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            onClick={() => {
              if (account?.id) {
                libraryLeaveMutation.mutate(
                  { accountId: account.id },
                  {
                    onError: (error) =>
                      showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                    onSuccess: () => {
                      showLibraryToast({
                        message: (
                          <>
                            Left <b className="text-jay-200">{library.title}</b> library.
                          </>
                        ),
                      });
                      onLeave();
                    },
                  },
                );
              }
            }}
            size="small"
          >
            Leave
          </Button>
        }
        className={classNames('border-b border-b-pigeon-100', className)}
        description={library.description}
        key={library.id}
        meta={meta || <Skeleton height={26} isText width={120} />}
        ref={ref}
        title={library.title}
      />
    );
  },
);

LibraryItemMemberContainer.displayName = 'LibraryItemContainer';
